import { FormInputProps } from '../../../validation';
import InputProps from '../InputProps';
import { FormValueTypes } from '../../../types';

/**
 * This method will generate a valid FormInputProps instance, a data structure returned
 * by the `getInputProps` method of `useFormHook`, using the parameters provided for the
 * input. The logic is as follows:
 * - If the input properties defines a `dataSource` value, it will use said value to
 *   create the returned data source.
 *   - If the input properties define an `onChange` method, it will overwrite the onChange
 *     defined by the dataSource. Note that this is the only parameter that does this, all
 *     other parameters are completly overwritten by the dataSource.
 * - If the `dataSource` prop can't be found, return the provided input values mapped to
 *   the right properties. Note that onChange will default to an empty method if nothing
 *   is provided in order to prevent raising unexpected exceptions.
 *
 * @param props - Properties provided to the input
 */
export default function generateProperties<
  T extends FormValueTypes,
  V extends FormValueTypes = T
>(props: InputProps<T, V>): FormInputProps<T, V> {
  const { inputName } = props;

  if (props.dataSource) {
    const dataSource = props.dataSource(inputName);
    // onChange is the only value where by default, props overwrite the dataSource.
    if (props.onChange) {
      dataSource.onChange = props.onChange;
    }

    return dataSource;
  }

  return {
    name: inputName,
    error: props.inputError,
    errorText: props.inputErrorMessage,
    value: props.defaultValue,
    onChange: props.onChange ? props.onChange : (): void => {},
  };
}
